import { useCallback, useContext, useState } from "react"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import { parseHybridJsonResponse } from "./parseHybridJsonResponse"
import i18n from "@/lib/i18n/i18n"
import { pickAdhocRoute } from "./cmrSearchRouting"
import { applyClientFilters } from "./cmrSearchClientFilters"
import type { UnlinkedDocument } from "./useCmrReportData"

export type CmrSearchInput = {
  dateStart: string
  dateEnd: string
  driverJid?: string
  roomJid?: string
  plates: string
  freeText: string
}

const ALL_DOCUMENTS_MAX = 500

export function useCmrSearch() {
  const { client } = useContext(XmppContext)
  const [results, setResults] = useState<UnlinkedDocument[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hitMaxCap, setHitMaxCap] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const runSearch = useCallback(
    async (input: CmrSearchInput) => {
      if (!client) return
      setIsLoading(true)
      setError(null)
      setHitMaxCap(false)

      try {
        const route = pickAdhocRoute({
          driverJid: input.driverJid,
          roomJid: input.roomJid,
        })

        let raw: Awaited<ReturnType<typeof AdHocCommands.getAllDocuments>>
        if (route.kind === "byUser") {
          raw = await AdHocCommands.getDocumentsByUser(
            client,
            route.userJid,
            input.dateStart,
            input.dateEnd,
          )
        } else if (route.kind === "byMuc") {
          raw = await AdHocCommands.getDocumentsByMuc(
            client,
            route.mucJid,
            input.dateStart,
            input.dateEnd,
          )
        } else {
          raw = await AdHocCommands.getAllDocuments(
            client,
            input.dateStart,
            input.dateEnd,
            ALL_DOCUMENTS_MAX,
          )
        }

        const parsed = await parseHybridJsonResponse<{
          documents?: UnlinkedDocument[]
        }>(raw)
        const docs = parsed.documents || []

        const filtered = applyClientFilters(docs, {
          plates: input.plates,
          freeText: input.freeText,
          narrowToRoomJid:
            route.kind === "byUser" ? route.narrowToRoomJid : undefined,
        })

        setResults(filtered)
        setHitMaxCap(route.kind === "all" && docs.length >= ALL_DOCUMENTS_MAX)
        setHasSearched(true)
      } catch (err) {
        console.error("Error running CMR search:", err)
        setError(
          err instanceof Error
            ? err.message
            : i18n.t("reports.cmrSearch.failed"),
        )
      } finally {
        setIsLoading(false)
      }
    },
    [client],
  )

  const reset = useCallback(() => {
    setResults([])
    setError(null)
    setHitMaxCap(false)
    setHasSearched(false)
  }, [])

  return {
    results,
    isLoading,
    error,
    hitMaxCap,
    hasSearched,
    runSearch,
    reset,
  }
}
